import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Copy as CopyIcon } from "lucide-react";

export default function AdminEmails() {
  const [logFilters, setLogFilters] = useState({ template: "", status: "" });
  const [queueFilter, setQueueFilter] = useState("");
  const [log, setLog] = useState({ items: [], total: 0 });
  const [queue, setQueue] = useState({ items: [], total: 0 });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const p = new URLSearchParams({ page_size: "100" });
      Object.entries(logFilters).forEach(([k, v]) => v && p.set(k, v));
      setLog((await api.get(`/admin/emails/log?${p.toString()}`)).data);
    } catch {}
    try {
      const p2 = new URLSearchParams({ page_size: "100" });
      if (queueFilter) p2.set("status", queueFilter);
      setQueue((await api.get(`/admin/emails/queue?${p2.toString()}`)).data);
    } catch {}
  };
  useEffect(() => { reload(); }, [logFilters, queueFilter]);

  const retry = async (qid) => {
    try { await api.post(`/admin/emails/queue/${qid}/retry`); toast.success("Retried"); reload(); }
    catch { toast.error("Retry failed"); }
  };
  const processAll = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/emails/process-queue`); toast.success(`Processed ${r.data.processed} (sent ${r.data.sent}, failed ${r.data.failed})`); reload(); }
    catch { toast.error("Process failed"); }
    finally { setBusy(false); }
  };
  const copy = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success("Copied"));

  return (
    <div className="space-y-8" data-testid="admin-emails-page">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <div><h1 className="font-serif text-3xl text-ink tracking-tight">Emails</h1><p className="text-ink-soft text-sm mt-1">Log {log.total} · Queue {queue.total}</p></div>
        <button onClick={processAll} disabled={busy} className="inline-flex items-center gap-2 border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5 disabled:opacity-60" data-testid="process-queue-btn">
          <RefreshCw className="h-3.5 w-3.5" /> Process queue now
        </button>
      </header>

      {/* Log */}
      <section className="space-y-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="font-serif text-xl text-ink">Log</h2>
          <input placeholder="template" value={logFilters.template} onChange={(e) => setLogFilters({ ...logFilters, template: e.target.value })}
            className="bg-bone border border-stone/40 px-2 py-1.5 text-xs" data-testid="log-template-filter" />
          <select value={logFilters.status} onChange={(e) => setLogFilters({ ...logFilters, status: e.target.value })}
            className="bg-bone border border-stone/40 px-2 py-1.5 text-xs" data-testid="log-status-filter">
            <option value="">All statuses</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
            <option value="mocked">mocked</option>
          </select>
        </div>
        <div className="border border-stone/40 bg-bone overflow-x-auto" data-testid="emails-log-table">
          <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-bone-dim border-b border-stone/40 text-[10px] uppercase tracking-[0.18em] text-stone">
            <div className="col-span-2">Date</div>
            <div className="col-span-3">Template</div>
            <div className="col-span-3">To</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-3">Dedupe</div>
          </div>
          {log.items.length === 0 ? <div className="px-3 py-5 text-sm text-ink-soft">No log entries.</div> : log.items.slice(0, 50).map((e) => (
            <div key={e.email_id} className="grid grid-cols-12 gap-3 px-3 py-2 border-b border-stone/20 items-baseline" data-testid={`log-row-${e.email_id}`}>
              <div className="col-span-2 text-[10px] text-stone font-mono">{(e.created_at || "").slice(5, 16)}</div>
              <div className="col-span-3 text-xs text-ink truncate">{e.template_key}</div>
              <div className="col-span-3 text-xs text-ink-soft truncate font-mono">{e.to}</div>
              <div className="col-span-1 text-[10px] uppercase tracking-[0.18em]">
                <span className={e.status === "sent" ? "text-ivy" : e.status === "failed" ? "text-brass" : "text-stone"}>{e.status}</span>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                <code className="text-[10px] font-mono text-ink-soft truncate">{e.dedupe_key}</code>
                <button onClick={() => copy(e.dedupe_key)} aria-label="Copy"><CopyIcon className="h-3 w-3 text-stone hover:text-ink" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Queue */}
      <section className="space-y-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="font-serif text-xl text-ink">Queue</h2>
          <select value={queueFilter} onChange={(e) => setQueueFilter(e.target.value)}
            className="bg-bone border border-stone/40 px-2 py-1.5 text-xs" data-testid="queue-status-filter">
            <option value="">All statuses</option>
            <option value="pending">pending</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
          </select>
        </div>
        <div className="border border-stone/40 bg-bone overflow-x-auto" data-testid="emails-queue-table">
          <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-bone-dim border-b border-stone/40 text-[10px] uppercase tracking-[0.18em] text-stone">
            <div className="col-span-2">Send at</div>
            <div className="col-span-3">Template</div>
            <div className="col-span-3">To</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2">Dedupe</div>
            <div className="col-span-1"></div>
          </div>
          {queue.items.length === 0 ? <div className="px-3 py-5 text-sm text-ink-soft">Queue empty.</div> : queue.items.map((q) => (
            <div key={q.queue_id} className="grid grid-cols-12 gap-3 px-3 py-2 border-b border-stone/20 items-baseline" data-testid={`queue-row-${q.queue_id}`}>
              <div className="col-span-2 text-[10px] text-stone font-mono">{(q.send_at || "").slice(5, 16)}</div>
              <div className="col-span-3 text-xs text-ink truncate">{q.template_key}</div>
              <div className="col-span-3 text-xs text-ink-soft truncate font-mono">{q.recipient_email}</div>
              <div className="col-span-1 text-[10px] uppercase tracking-[0.18em]">
                <span className={q.status === "sent" ? "text-ivy" : q.status === "failed" ? "text-brass" : "text-stone"}>{q.status}</span>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <code className="text-[10px] font-mono text-ink-soft truncate">{q.dedupe_key}</code>
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => retry(q.queue_id)} className="text-[10px] uppercase tracking-[0.22em] text-ivy hover:text-brass" data-testid={`queue-retry-${q.queue_id}`}>Retry</button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
